import * as React from "react";
import { Gauge, Hash, Star, ThumbsUp, type LucideIcon } from "lucide-react";
import type { ReviewType } from "@/services/bedrock";
import { cn } from "@/lib/utils";

const REVIEW_RATING_TYPE_CONFIG: Record<
  ReviewType,
  {
    title: string;
    Icon: LucideIcon;
    iconProps?: { strokeWidth?: number; className?: string };
    rowActiveClass: string;
    rowIdleClass: string;
  }
> = {
  star: {
    title: "Stars",
    Icon: Star,
    iconProps: { strokeWidth: 0, className: "fill-current" },
    rowActiveClass: "bg-amber-400 border-amber-400 text-white",
    rowIdleClass: "bg-white border-slate-200 text-amber-400 hover:border-amber-300 hover:bg-amber-50",
  },
  nps: {
    title: "NPS",
    Icon: Gauge,
    rowActiveClass: "bg-emerald-600 border-emerald-600 text-white",
    rowIdleClass: "bg-white border-slate-200 text-emerald-600 hover:border-emerald-300 hover:bg-emerald-50",
  },
  ten_point: {
    title: "10-Point",
    Icon: Hash,
    rowActiveClass: "bg-indigo-500 border-indigo-500 text-white",
    rowIdleClass: "bg-white border-slate-200 text-indigo-500 hover:border-indigo-300 hover:bg-indigo-50",
  },
  text: {
    title: "Recommendation",
    Icon: ThumbsUp,
    rowActiveClass: "bg-sky-500 border-sky-500 text-white",
    rowIdleClass: "bg-white border-slate-200 text-sky-500 hover:border-sky-300 hover:bg-sky-50",
  },
};

const OPTIONS = (["star", "nps", "ten_point", "text"] as ReviewType[]).map(id => ({
  id,
  ...REVIEW_RATING_TYPE_CONFIG[id],
}));

type ReviewRatingTypeFilterButtonsProps = {
  value: ReviewType | null;
  onChange: (value: ReviewType | null) => void;
};

export function ReviewRatingTypeFilterButtons({ value, onChange }: ReviewRatingTypeFilterButtonsProps) {
  const [hoveredId, setHoveredId] = React.useState<ReviewType | null>(null);
  const [focusedId, setFocusedId] = React.useState<ReviewType | null>(null);
  const activeId = hoveredId ?? focusedId;

  return (
    <div
      className="flex h-14 w-full gap-1.5"
      role="group"
      aria-label="Rating Type"
      onMouseLeave={() => setHoveredId(null)}
    >
      {OPTIONS.map(({ id, title, Icon, iconProps, rowActiveClass, rowIdleClass }) => {
        const selected = value === id;
        const showLabel = activeId === id || (selected && activeId == null);

        return (
          <button
            key={id}
            type="button"
            aria-pressed={selected}
            aria-label={title}
            title={title}
            className={cn(
              "flex h-full min-w-0 flex-col items-center justify-center overflow-hidden rounded-lg border px-1.5 transition-[flex-grow,flex-basis,colors] duration-300 ease-out cursor-pointer",
              showLabel ? "flex-[5] basis-0" : "flex-1 basis-0",
              selected ? rowActiveClass : rowIdleClass
            )}
            onMouseEnter={() => setHoveredId(id)}
            onFocus={() => setFocusedId(id)}
            onBlur={() => setFocusedId(current => (current === id ? null : current))}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onChange(selected ? null : id);
            }}
          >
            <Icon
              className={cn("size-5 shrink-0 pointer-events-none", iconProps?.className)}
              strokeWidth={iconProps?.strokeWidth}
            />
            <span
              className={cn(
                "grid w-full transition-[grid-template-rows,opacity] duration-300 ease-out",
                showLabel ? "grid-rows-[1fr] opacity-100 mt-1" : "grid-rows-[0fr] opacity-0 mt-0"
              )}
              aria-hidden={!showLabel}
            >
              <span className="min-h-0 overflow-hidden">
                <span className="block truncate text-[10px] font-bold uppercase tracking-wide leading-none pointer-events-none text-center">
                  {title}
                </span>
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
